import getIn from 'lodash/get';
import invert from 'lodash/invert';
import memoize from 'lodash/memoize';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import * as PaymentsConstants from '../constants';
import * as D from '../deps';

import { getClientProfile, initClientProfiler, renewClientProfile } from './clientProfiler';

const INTL_STYLE = 'currency';

const log = D.Logger.child({ component: 'domains/payments/utils' });

export { getClientProfile, initClientProfiler, renewClientProfile };

export function getBackingProduct(productGroup, durationType) {
  const backingProducts =
    productGroup && productGroup.get(PaymentsConstants.ProductKeys.BACKING_PRODUCTS);
  if (backingProducts) {
    return backingProducts.find(bp => {
      return bp.get(PaymentsConstants.ProductKeys.DURATION_TYPE) === durationType;
    });
  }
  return null;
}

// Gets the specific backing product price unformatted
export function getBackingProductPrice(backingProduct) {
  if (backingProduct) {
    return backingProduct.get(PaymentsConstants.ProductKeys.PRICE);
  }
}

export function isAdSupportedTier(tier) {
  return tier === PaymentsConstants.tiers.AD_SUPPORTED;
}

export function isPlusTier(tier) {
  return tier === PaymentsConstants.tiers.PANDORA_PLUS;
}

export function isPremiumTier(tier) {
  return tier === PaymentsConstants.tiers.PANDORA_PREMIUM;
}

export function isPremiumFamilyTier(tier) {
  return tier === PaymentsConstants.tiers.PANDORA_FAMILY;
}

export function isPremiumPlan(tier) {
  return isPremiumFamilyTier(tier) || isPremiumTier(tier);
}

export function isDiscount(discountType) {
  return discountType && discountType !== PaymentsConstants.discountTypes.NONE;
}

export function isPaypal(paymentProvider) {
  return paymentProvider === PaymentsConstants.PaymentProviders.PAYPAL;
}

export function getCardType(accountNumber) {
  let cardType = null;

  if (!accountNumber.value) {
    return cardType;
  }

  if (accountNumber.value.match(/^4/) != null) {
    cardType = PaymentsConstants.CARD_COMPANIES.VISA;
  } else if (accountNumber.value.match(/^(34|37)/) != null) {
    cardType = PaymentsConstants.CARD_COMPANIES.AMERICAN_EXPRESS;
  } else if (accountNumber.value.match(/^(5[1-5]|2[2-7])/) != null) {
    cardType = PaymentsConstants.CARD_COMPANIES.MASTERCARD;
  } else if (isDiscoverCard(accountNumber.value)) {
    cardType = PaymentsConstants.CARD_COMPANIES.DISCOVER;
  }

  return cardType;
}

export function isDiscoverCard(value) {
  // values taken from "Discover IIN Range Summary" public doc
  // ..with contiguous ranges consolidated
  const valuePrefix = value.replace(/\s/g, '').substring(0, 8);
  return [
    { min: '30000000', max: '30599999' },
    { min: '30880000', max: '31029999' },
    { min: '31120000', max: '31209999' },
    { min: '31580000', max: '31599999' },
    { min: '33370000', max: '33499999' },
    { min: '35280000', max: '35899999' },
    { min: '36000000', max: '36999999' },
    { min: '38000000', max: '39999999' },
    { min: '60110000', max: '60110999' },
    { min: '60112000', max: '60114999' },
    { min: '60117400', max: '60117499' },
    { min: '60117700', max: '60117999' },
    { min: '60118600', max: '60119999' },
    { min: '62109400', max: '62109499' },
    { min: '62212600', max: '62379699' },
    { min: '62400000', max: '62699999' },
    { min: '62820000', max: '62889999' },
    { min: '64400000', max: '65999999' },
    { min: '81000000', max: '81719999' },
  ].reduce((acc, curr) => acc || (valuePrefix >= curr.min && valuePrefix <= curr.max), false);
}

// Blackhawk gift cards are a subset of Visa cards, so getCardType categorizes as Visa for acquireProduct
// ..this utility is used when we need to specifically tell it's a Blackhawk card.
export function isBlackhawkCard(value) {
  const strippedValue = value && value.replace(/\s/g, '');
  const valuePrefix =
    strippedValue && strippedValue.length === 16 && strippedValue.substring(0, 10);
  return valuePrefix >= '4436133148' && valuePrefix <= '4436133150';
}

export function hasInitialProduct(
  productGroup,
  tierToCheck,
  discountTypeToCheck = PaymentsConstants.discountTypes.NONE,
) {
  const initialProduct = productGroup.get(PaymentsConstants.ProductKeys.INITIAL_PRODUCT);

  if (!initialProduct) {
    return false;
  }

  const productTier = initialProduct.get(PaymentsConstants.ProductKeys.PRODUCT_TIER);
  const discountType = initialProduct.get(
    PaymentsConstants.ProductKeys.DISCOUNT_TYPE,
    PaymentsConstants.discountTypes.NONE,
  );

  return (
    productTier === tierToCheck &&
    discountType === discountTypeToCheck &&
    [
      PaymentsConstants.SubscriptionKeys.TRIAL_SUBSCRIPTION,
      PaymentsConstants.SubscriptionKeys.PAYMENT_BACKED_CODE,
    ].includes(initialProduct.get(PaymentsConstants.ProductKeys.PRODUCT_TYPE))
  );
}

// Gets the vendor SKU for a product group. We need this to pass back into billing.acquireProduct
export function getVendorSku(productGroup) {
  return productGroup.get(PaymentsConstants.ProductKeys.VENDOR_SKU);
}

/**
 * The `daysUntilNextBillingDate` property of the subscriptionInfo object is unreliable because it uses a rolling 24hr clock
 * where an expiration that is tomorrow but less than 24hrs away is treated as today.
 * As a result, we calculate the "days until" ourselves using "nextBillingDate".
 *
 * @param {string} nextBillingDate
 * @returns {number}
 */
export function getDaysUntilNextBillingDate(nextBillingDate) {
  return D.Utils.Time.getDaysAgo(new Date(nextBillingDate), new Date());
}

export function formatDate(intl, dateString) {
  return intl.formatDate(dateString, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
}
export function calculateExpiryDateForCurrentTier(expirationDate, pendingGiftDurationInfo) {
  const finalExpirationDate = new Date(expirationDate);
  pendingGiftDurationInfo.forEach(gift => {
    const { durationType, durationTime } = gift;
    switch (durationType) {
      case PaymentsConstants.DurationTypes.MONTHLY:
        finalExpirationDate.setMonth(finalExpirationDate.getMonth() - durationTime);
        break;
      case PaymentsConstants.DurationTypes.DAILY:
        finalExpirationDate.setDate(finalExpirationDate.getDate() - durationTime);
        break;
      case PaymentsConstants.DurationTypes.WEEKLY:
        finalExpirationDate.setDate(finalExpirationDate.getDate() - durationTime * 7);
        break;
      case PaymentsConstants.DurationTypes.HOURLY:
        finalExpirationDate.setHours(finalExpirationDate.getHours() - durationTime);
        break;
      case PaymentsConstants.DurationTypes.YEARLY:
        finalExpirationDate.setFullYear(finalExpirationDate.getFullYear() - durationTime);
        break;
    }
  });
  return finalExpirationDate;
}

export function getTierSlugFromTier(tier) {
  return Object.keys(PaymentsConstants.planSlugToDataMap).find(slug => {
    return (
      PaymentsConstants.planSlugToDataMap[slug].tier === tier &&
      PaymentsConstants.planSlugToDataMap[slug].discountType
    );
  });
}

export function getTierFromSlugs(tierSlug, subTierSlug) {
  const obj =
    PaymentsConstants.planSlugToDataMap[subTierSlug] ||
    PaymentsConstants.planSlugToDataMap[tierSlug];
  if (obj) {
    return obj.tier;
  }
}

export function getDiscountTypeFromSubTierSlug(subTierSlug) {
  if (!PaymentsConstants.planSlugToDataMap[subTierSlug]) {
    return PaymentsConstants.discountTypes.NONE;
  }

  return PaymentsConstants.planSlugToDataMap[subTierSlug].discountType;
}

export function getOfferSlugs(tier = '', discountType = '') {
  if (tier === PaymentsConstants.tiers.AD_SUPPORTED) {
    return {
      tierSlug: '',
      subTierSlug: '',
    };
  }

  if (tier === PaymentsConstants.tiers.PANDORA_FAMILY) {
    return {
      tierSlug: PaymentsConstants.planSlugs.premium,
      subTierSlug: PaymentsConstants.planSlugs.familyPlan,
    };
  }

  const tempArray = tier.split('_');
  const tierSlug = tempArray.length === 2 ? tempArray[1].toLowerCase() : '';
  const subTierSlug =
    tierSlug && discountType !== PaymentsConstants.discountTypes.NONE
      ? discountType.toLowerCase()
      : '';

  return {
    tierSlug,
    subTierSlug,
  };
}

export function compareTiers(currentProductTier, newProductTier) {
  // default to upgrade if data is missing/invalid
  return newProductTier in PaymentsConstants.PANDORA_TIER_RANKS &&
    currentProductTier in PaymentsConstants.PANDORA_TIER_RANKS
    ? PaymentsConstants.PANDORA_TIER_RANKS[newProductTier] -
        PaymentsConstants.PANDORA_TIER_RANKS[currentProductTier]
    : 1;
}

export function isUpgrade(currentProductTier, newProductTier) {
  return compareTiers(currentProductTier, newProductTier) > 0;
}

export function isDowngrade(currentProductTier, newProductTier) {
  return compareTiers(currentProductTier, newProductTier) < 0;
}

export function isSidegrade(
  currentProductTier,
  currentProductDiscountType,
  newProductTier,
  newProductDiscountType,
) {
  return (
    compareTiers(currentProductTier, newProductTier) === 0 &&
    currentProductDiscountType !== newProductDiscountType
  );
}

export function isAuthorizedSidegrade(
  currentProductTier,
  currentProductDiscountType,
  newProductTier,
  newProductDiscountType,
) {
  return (
    isSidegrade(
      currentProductTier,
      currentProductDiscountType,
      newProductTier,
      newProductDiscountType,
    ) && newProductDiscountType !== PaymentsConstants.discountTypes.NONE
  );
}

export function isSameProduct(
  currentProductTier,
  currentProductDiscountType,
  newProductTier,
  newProductDiscountType,
) {
  return (
    compareTiers(currentProductTier, newProductTier) === 0 &&
    currentProductDiscountType === newProductDiscountType
  );
}

// used for filtering products on upgrade pages
export function shouldShowAsUpgrade(
  currentProductTier,
  currentProductDiscountType,
  newProductTier,
  newProductDiscountType,
) {
  return (
    isUpgrade(currentProductTier, newProductTier) ||
    (isSidegrade(
      currentProductTier,
      currentProductDiscountType,
      newProductTier,
      newProductDiscountType,
    ) &&
      !(
        currentProductDiscountType !== PaymentsConstants.discountTypes.NONE &&
        newProductDiscountType === PaymentsConstants.discountTypes.NONE
      ))
  );
}

export function isHighestRankedTier(tier) {
  return PaymentsConstants.PANDORA_TIER_RANKS[tier] === Object.keys(PaymentsConstants.tiers).length;
}

export function getBrandingIcon(tier, discountType) {
  let brandingIcon = null;
  if (
    tier === PaymentsConstants.tiers.PANDORA_PREMIUM &&
    discountType &&
    discountType !== PaymentsConstants.discountTypes.NONE
  ) {
    brandingIcon = PaymentsConstants.pandoraPremiumDiscountTypeToBrandingIcon[discountType];
  } else {
    brandingIcon = PaymentsConstants.pandoraTierToBrandingIcon[tier];
  }
  return (
    brandingIcon ||
    PaymentsConstants.pandoraTierToBrandingIcon[PaymentsConstants.tiers.AD_SUPPORTED]
  );
}

function getDoubleLookupOrDefault(index1, index2, map, defaultValue) {
  if (map && map[index1]) {
    return map[index1][index2] || defaultValue;
  }
  return defaultValue;
}

export function getPlanName(tier, discountType = PaymentsConstants.discountTypes.NONE) {
  return getDoubleLookupOrDefault(tier, discountType, PaymentsConstants.planNames, '');
}

export function getViewModePlanName(tier, discountType = PaymentsConstants.discountTypes.NONE) {
  const planName = getDoubleLookupOrDefault(tier, discountType, PaymentsConstants.planNames, '');
  return planName ? planName.toLowerCase().replace(/ /g, '_') : null;
}

export function getPlanWebRegistration(tier, discountType) {
  return getDoubleLookupOrDefault(tier, discountType, PaymentsConstants.planWebRegistrationMap, '');
}

export function getPlanMobileLanding(tier, discountType) {
  return getDoubleLookupOrDefault(tier, discountType, PaymentsConstants.planMobileLandingMap, '');
}

const blankUpgradeCardData = {};
export function getUpgradeCardData(tier, discountType) {
  return getDoubleLookupOrDefault(
    tier,
    discountType,
    PaymentsConstants.Upgrade.UpgradeCardData,
    blankUpgradeCardData,
  );
}

export function getUpgradeCardDataField(tier, discountType, field) {
  const upgradeCardData = getUpgradeCardData(tier, discountType);
  return upgradeCardData && field in upgradeCardData ? upgradeCardData[field] : '';
}

// discountType is not at the top level of a product group
// so we have to reach down into the initial or backing products to find it
export function getDiscountTypeFromProductGroup(productGroup) {
  const initialProduct =
    productGroup && productGroup.get(PaymentsConstants.ProductKeys.INITIAL_PRODUCT);
  let result = initialProduct && initialProduct.get(PaymentsConstants.ProductKeys.DISCOUNT_TYPE);
  if (!result) {
    const backingProducts =
      productGroup && productGroup.get(PaymentsConstants.ProductKeys.BACKING_PRODUCTS);
    result =
      backingProducts &&
      backingProducts.size &&
      backingProducts.getIn([0, PaymentsConstants.ProductKeys.DISCOUNT_TYPE]);
  }
  return result || PaymentsConstants.discountTypes.NONE;
}

// this is memoized so it can be used to return selector results directly
export const formatDisplayablePrice = memoize(
  (price, currency) => {
    let result = <FormattedMessage {...PaymentsConstants.Messages.free} />;
    if (price) {
      if (currency !== PaymentsConstants.US_CURRENCY) {
        // log error, but default to USD anyway, we have no alternative
        log.error(`attempt to use unsupported non-US currency: ${currency} for price: ${price}`);
      }
      result = D.Utils.StringUtils.formatPrice(price, 'en-US');
    }
    return result;
  },
  (price, currency) => price + currency,
);

export function formatDisplayableDuration(durationTime, durationType) {
  if (Number.isInteger(durationTime) && durationType in PaymentsConstants.DurationTypes) {
    const formattedDurationType = (
      <FormattedMessage
        {...PaymentsConstants.DurationTypeMessages[durationType]}
        values={{ durationTime }}
      />
    );
    return (
      <FormattedMessage
        {...PaymentsConstants.DurationTypeMessages[PaymentsConstants.DURATION_FORMAT]}
        values={{
          durationTime,
          durationTypeMessage: formattedDurationType,
        }}
      />
    );
  }
  return '';
}

export function getDefaultPrice({ tier, discountType, durationType }) {
  return PaymentsConstants.DefaultPlanData.getIn([
    tier,
    discountType,
    PaymentsConstants.productTypes.SUBSCRIPTION,
    durationType,
    'price',
  ]);
}

export function getPaymentFrequencyForDurationType(durationType) {
  return PaymentsConstants.PaymentFrequencies[durationType];
}

export function getDurationTypeFromPaymentFrequency(frequency) {
  const paymentFrequencyToDurationType = invert(PaymentsConstants.PaymentFrequencies);
  return paymentFrequencyToDurationType[frequency];
}

export function formatPrice(intl, price, currency) {
  return intl.formatNumber(price / 100, { style: INTL_STYLE, currency });
}

export function getParsedPartnerName(partnerName = '') {
  // Special characters are safe to pass to the stats tables
  return partnerName.toUpperCase();
}

export function getParsedTierName(tier = '') {
  // TODO remove or update with WEBCLIENT-6975
  return tier.toLowerCase().replace(' ', '_');
}

export function getDataMapUpgradeTier(tier = '') {
  // TODO remove or update with WEBCLIENT-6975
  const tierSlug = tier.toLowerCase().replace(' ', '-');
  return PaymentsConstants.planSlugToDataMap[tierSlug].tier;
}

export function getStatsPartnerUpgradePageView({ partner, tier }) {
  const partnerName = getParsedPartnerName(partner);
  const upgradeTier = getParsedTierName(tier);
  return `${partnerName}_${upgradeTier}${PaymentsConstants.PageViewSuffixes.PARTNER_SUBSCRIPTION_LANDING}`;
}

export function getStatsPartnerConfirmationPageView(partnerName) {
  return `${partnerName}_${PaymentsConstants.PageViewSuffixes.UPGRADE_SUCCESS}`;
}

export function getAnnualPricingMessage(discountType) {
  return discountType === PaymentsConstants.discountTypes.STUDENT
    ? PaymentsConstants.Messages.annualNotBest
    : PaymentsConstants.Messages.annual;
}

// utilities for upgrade components
export function getIsUpgradePageReady(discountType, areDependenciesLoaded) {
  return isDiscount(discountType) ? areDependenciesLoaded : true;
}

export function getValidDiscountTypeWithDiscountOffer(discountType, isPageReady, hasDiscountOffer) {
  return isDiscount(discountType) && isPageReady ? hasDiscountOffer : true;
}

export function getPlanBlurb(tier, discountType = PaymentsConstants.discountTypes.NONE) {
  if (!(tier in PaymentsConstants.PlanBlurbs)) {
    return '';
  }
  return PaymentsConstants.PlanBlurbs[tier][discountType];
}

export function isDirectBilledUser(isVendorBilled, isPartnerBilled, isNonPaymentBackedUser) {
  return !isVendorBilled && !isPartnerBilled && !isNonPaymentBackedUser;
}

export function isReady({
  isSubscriptionInfoLoaded,
  isAvailableProductsLoaded,
  isCreditCardLoaded,
  isPendingPurchase,
  paymentProvider,
}) {
  return (
    !isPendingPurchase &&
    isAvailableProductsLoaded &&
    isSubscriptionInfoLoaded &&
    (paymentProvider !== PaymentsConstants.PaymentProviders.CREDIT_CARD ||
      (paymentProvider === PaymentsConstants.PaymentProviders.CREDIT_CARD && isCreditCardLoaded))
  );
}

export function getSpecialOfferErrorViewMode(errorCode) {
  return 'ineligible_' + errorCode.toLowerCase();
}

export function getTermsLinkText(discountType) {
  const termsLinkText = {
    [PaymentsConstants.discountTypes.STUDENT]: PaymentsConstants.Messages.studentTermsLink,
    [PaymentsConstants.discountTypes.MILITARY]: PaymentsConstants.Messages.militaryTermsLink,
    [PaymentsConstants.discountTypes.NONE]: PaymentsConstants.Messages.termsLink,
  };

  return termsLinkText[discountType];
}

export function getTermsUrl(discountType) {
  const termsUrls = {
    [PaymentsConstants.discountTypes.STUDENT]: D.Utils.Route.getStudentTermsUrl(),
    [PaymentsConstants.discountTypes.MILITARY]: D.Utils.Route.getMilitaryTermsUrl(),
    [PaymentsConstants.discountTypes.NONE]: D.Utils.Route.getTermsUrl(),
  };

  return termsUrls[discountType];
}

export function getUpgradePath(tier, discountType) {
  const { tierSlug, subTierSlug } = getOfferSlugs(tier, discountType);

  return D.Utils.Route.getUpgradePath(tierSlug, subTierSlug);
}

export function getPaymentPath(tier, discountType) {
  const { tierSlug, subTierSlug } = getOfferSlugs(tier, discountType);

  return D.Utils.Route.getPaymentPath(tierSlug, subTierSlug);
}

export function shouldRedirectFromUpgradeToSubscription(
  activeTier,
  activeDiscountType,
  upgradeTier,
  upgradeDiscountType,
  isGiftee,
  isComp,
  isDiscountVerificationRequired,
) {
  // @todo [WEBCLIENT-7276] - remove the isComp check
  if (isComp) {
    return true;
  }

  const isGifteeSidegrade =
    isGiftee && isSameProduct(activeTier, activeDiscountType, upgradeTier, upgradeDiscountType);
  if (isGifteeSidegrade) {
    return false;
  }

  const isStudentReverification =
    activeDiscountType === PaymentsConstants.discountTypes.STUDENT &&
    upgradeDiscountType === PaymentsConstants.discountTypes.STUDENT &&
    isDiscountVerificationRequired;
  if (isStudentReverification) {
    return false;
  }

  return (
    (upgradeTier &&
      upgradeDiscountType &&
      !shouldShowAsUpgrade(activeTier, activeDiscountType, upgradeTier, upgradeDiscountType)) ||
    isHighestRankedTier(activeTier)
  );
}

export function getSpecialOfferErrorData(specialOfferStatus) {
  return D.getInDomain('payments.utils._specialOffers_.getSpecialOfferErrorData')(
    specialOfferStatus,
  );
}

export function getVerificationTypeFromDiscountType(discountType) {
  return D.getInDomain('payments.utils._verification_.getVerificationTypeFromDiscountType')(
    discountType,
  );
}

export function getPaymentProviderRef(paymentProvider) {
  return PaymentsConstants.PaymentProviderRefs[paymentProvider] || '';
}

// if the server sends an error of the form:
// { errorMessage: "REAL_ERROR_TYPE|Real error message", errorType: 'SUBSCRIPTION_SERVICE_EXCEPTION' }
// ..we have to parse the real type & message out of the passed errorMessage
export function parseErrorTypeAndMessage(error) {
  let errorType = getIn(error, 'errorType', '');
  let errorMessage = getIn(error, 'errorMessage', '');
  if (errorType === PaymentsConstants.ErrorCodes.SUBSCRIPTION_SERVICE_EXCEPTION) {
    const barIndex = errorMessage.indexOf('|');
    if (barIndex >= 0) {
      errorType = errorMessage.substring(0, barIndex);
      errorMessage = errorMessage.substring(barIndex + 1);
    }
  }
  return { errorType, errorMessage };
}

export function calculateTrialExpiryDate(
  currentDate,
  trialDurationTime,
  trialDurationType,
  trialTimesRenewable,
) {
  const finalExpirationDate = currentDate;
  const trialMultiplier = trialTimesRenewable + 1;
  switch (trialDurationType) {
    case PaymentsConstants.DurationTypes.MONTHLY:
      finalExpirationDate.setMonth(
        finalExpirationDate.getMonth() + trialDurationTime * trialMultiplier,
      );
      break;
    case PaymentsConstants.DurationTypes.DAILY:
      finalExpirationDate.setDate(
        finalExpirationDate.getDate() + trialDurationTime * trialMultiplier,
      );
      break;
    case PaymentsConstants.DurationTypes.WEEKLY:
      finalExpirationDate.setDate(
        finalExpirationDate.getDate() + trialDurationTime * 7 * trialMultiplier,
      );
      break;
    case PaymentsConstants.DurationTypes.HOURLY:
      finalExpirationDate.setHours(
        finalExpirationDate.getHours() + trialDurationTime * trialMultiplier,
      );
      break;
    case PaymentsConstants.DurationTypes.YEARLY:
      finalExpirationDate.setFullYear(
        finalExpirationDate.getFullYear() + trialDurationTime * trialMultiplier,
      );
      break;
  }
  return finalExpirationDate;
}

export function getDisplayableBillingDate(
  trialDurationTime,
  trialDurationType,
  trialTimesRenewable,
  intl,
) {
  const date = calculateTrialExpiryDate(
    new Date(),
    trialDurationTime,
    trialDurationType,
    trialTimesRenewable,
  );
  date.setDate(date.getDate());
  return formatDate(intl, date);
}

export function getAutoRenewalDisclaimer(autoRenewalDisclaimerValues) {
  const {
    trialPrice,
    durationType,
    displayableBackingPrice,
    offerTrialType,
    trialTimesRenewable,
    displayableDurationType,
    displayableTrialDuration,
    offerDurationTime,
    offerDurationType,
    activeProductExpirationDate,
    activeProductNextBillingDate,
    activeProductPrice,
    intl,
    locale,
    isGiftee,
  } = autoRenewalDisclaimerValues;
  const hasIntroductoryPricing = !!trialPrice;
  const hasFreeTrial = trialPrice === 0;
  const displayableTrialPrice = D.Utils.StringUtils.returnPriceString(trialPrice, locale);
  if (isGiftee) {
    const values = {
      dayAfterExpirationDate: formatDate(intl, activeProductExpirationDate),
      timeUnit: displayableDurationType,
      price: displayableBackingPrice,
    };
    return (
      <FormattedMessage
        {...PaymentsConstants.Messages.autoRenewalDisclaimerStartLater}
        values={values}
      />
    );
  }
  if (hasIntroductoryPricing) {
    // has intro price
    let sixMonthTrialExpirationDate;
    let sixMonthDisplayableBillingDate;
    let is6MPremiumTrialExpiringAfterActiveProduct;
    if (offerTrialType === PaymentsConstants.OfferTrialTypes.SIX_MONTH_ANNUAL_PREMIUM) {
      // has six_month_annual_premium
      sixMonthTrialExpirationDate = calculateTrialExpiryDate(
        new Date(),
        6,
        PaymentsConstants.DurationTypes.MONTHLY,
        0,
      );
      sixMonthDisplayableBillingDate = getDisplayableBillingDate(
        6,
        PaymentsConstants.DurationTypes.MONTHLY,
        0,
        intl,
      );
      is6MPremiumTrialExpiringAfterActiveProduct =
        sixMonthTrialExpirationDate > new Date(activeProductNextBillingDate);

      if (is6MPremiumTrialExpiringAfterActiveProduct) {
        const values = {
          priorTierPrice: D.Utils.StringUtils.returnPriceString(activeProductPrice, intl.locale),
          currentRenewalDate:
            activeProductNextBillingDate && formatDate(intl, activeProductNextBillingDate),
          price: displayableBackingPrice,
          timeUnit: displayableDurationType,
          dayAfterExpirationDate:
            sixMonthDisplayableBillingDate ||
            getDisplayableBillingDate(
              offerDurationTime,
              offerDurationType,
              trialTimesRenewable,
              intl,
            ),
        };

        return (
          <FormattedMessage
            {...PaymentsConstants.Messages
              .autoRenewalDisclaimer6MonthAnnualPremiumExpiringAfterActiveProduct}
            values={values}
          />
        );
      }
      const values = {
        timeUnit: displayableDurationType,
        price: displayableBackingPrice,
        dayAfterExpirationDate:
          sixMonthDisplayableBillingDate ||
          getDisplayableBillingDate(
            offerDurationTime,
            offerDurationType,
            trialTimesRenewable,
            intl,
          ),
      };

      return (
        <FormattedMessage
          {...PaymentsConstants.Messages.autoRenewalDisclaimerStartLater}
          values={values}
        />
      );
    }

    const values = {
      initialProductPrice: displayableTrialPrice,
      timeUnit: displayableDurationType,
      plural: trialTimesRenewable ? 's' : '',
      trialTimesRenewable: trialTimesRenewable + 1,
      initialProductDurationTime: displayableTrialDuration,
      backingProductPrice: displayableBackingPrice,
      dayAfterExpirationDate: getDisplayableBillingDate(
        offerDurationTime,
        offerDurationType,
        trialTimesRenewable,
        intl,
      ),
    };
    return (
      <FormattedMessage
        {...PaymentsConstants.Messages.autoRenewalDisclaimerStartTodayWithIntroPrice}
        values={values}
      />
    );
  } else if (hasFreeTrial) {
    // has free trial
    const values = {
      timeUnit: displayableDurationType,
      price: displayableBackingPrice,
      dayAfterExpirationDate: getDisplayableBillingDate(
        offerDurationTime,
        offerDurationType,
        trialTimesRenewable,
        intl,
      ),
    };
    return (
      <FormattedMessage
        {...PaymentsConstants.Messages.autoRenewalDisclaimerStartLater}
        values={values}
      />
    );
  }
  // no intro price or free trial or active product, sub start today at backing price
  const values = {
    displayableDurationType: durationType ? durationType.toLowerCase() : durationType,
    price: displayableBackingPrice,
  };
  return (
    <FormattedMessage
      {...PaymentsConstants.Messages.autoRenewalDisclaimerStartTodayWithBackingPrice}
      values={values}
    />
  );
}
